import React, { useCallback, useState } from "react";
import { AddRoadOutlined, DirectionsRailwayOutlined, HolidayVillageOutlined, PictureInPictureOutlined, WaterOutlined } from "@mui/icons-material";
import type { SvgIconProps } from "@mui/material";
import { Alert, AlertTitle, Box, Button, Typography } from "@mui/material";

import { LoadingButton, Table, TableBody, TableCell, TableHead, TableRow } from "ui";
import Progress from "ui/Progress/Progress";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { ConfirmModal, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

const stageConfig: Record<string, { label: string }> = {
  export: { label: "Exporting base data" },
  clean: { label: "Cleaning data" },
  refactor: { label: "Refactoring data" },
};

const importItems: Array<{
  name: string;
  icon: React.ComponentType<SvgIconProps>;
  type: string;
  iconColor?: SvgIconProps["color"];
}> = [
  { name: "Addresses", icon: HolidayVillageOutlined, type: "Input data" },
  { name: "Parcel", icon: PictureInPictureOutlined, type: "Input data" },
  { name: "Streets", icon: AddRoadOutlined, type: "Input data" },
  { name: "Waterways", icon: WaterOutlined, type: "Reference data", iconColor: "primary" },
  { name: "Railways", icon: DirectionsRailwayOutlined, type: "Reference data", iconColor: "primary" },
];

interface BoundaryImportProps {
  hasExistingData?: boolean;
}

const BoundaryImport: React.FC<BoundaryImportProps> = ({ hasExistingData = false }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { clear } = useStackedNavigationContext();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleOnComplete = useCallback(async () => {
    clear();
  }, [clear]);

  const { workflowState, stages, runWorkflow } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.ISG.EXTRACT,
    onComplete: handleOnComplete,
  });
  const isInProgress = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const proceedWithImport = useCallback(() => {
    runWorkflow?.();
    setShowConfirmModal(false);
  }, [runWorkflow]);

  const handleOnImport = useCallback(() => {
    if (hasExistingData) {
      setShowConfirmModal(true);
    } else {
      proceedWithImport();
    }
  }, [hasExistingData, proceedWithImport]);

  const handleCancelImport = () => {
    setShowConfirmModal(false);
  };

  const onBack = () => {
    if (!isInProgress) {
      clear();
    }
  };

  return (
    <Box>
      <StackedNavigationHeader title="Import data" onBackButtonClick={onBack} />
      <Typography fontWeight="500">Boundary</Typography>
      <ConfirmModal
        open={showConfirmModal}
        header="Replace existing data?"
        confirmText="Continue"
        cancelText="Cancel"
        color="error"
        content={
          <Typography variant="body2">
            This will replace existing imported layer data (addresses, parcels, streets, waterways, railways). This action cannot be undone.
          </Typography>
        }
        onConfirm={proceedWithImport}
        onCancel={handleCancelImport}
      />

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ my: 1 }}>
          <AlertTitle>Data import failed</AlertTitle>
          There was an error running data import. Please try again.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ my: 1 }}>
          Data import complete.
        </Alert>
      )}

      {stages && (isInProgress || workflowState === "ERROR") && (
        <Box sx={{ mb: 2 }}>
          <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
        </Box>
      )}

      <Typography variant="body3" sx={{ mb: 1, display: "block" }}>
        Data that will be imported based on your boundary:
      </Typography>
      <Table size="small" variant="ag-grid-compact">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {importItems.map(({ name, icon: Icon, type, iconColor = "secondary" }) => (
            <TableRow key={name}>
              <TableCell sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Icon color={iconColor} sx={{ width: 18, height: 18 }} />
                <Box component="span" sx={{ fontWeight: "500" }}>
                  {name}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="subtle">{type}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
        <Button color="primary" onClick={onBack} size="small" disabled={isInProgress}>
          Cancel
        </Button>
        <LoadingButton color="primary" variant="contained" onClick={handleOnImport} size="small" disabled={isInProgress} loading={isInProgress}>
          Import
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default BoundaryImport;
